import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAITTheme } from '@this/shared/ui/theme';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { useDebugLogContext, type DebugLogCategory, type DebugLogEntry } from './DebugLogContext';
import { sendDebugReport } from '../api/debugReport';
import { fetchConversationState, type ConversationState, type SearchItem } from '../api/conversationState';
import type { ProposalBundle } from '../api';
import { getStyles } from './DebugPanelStyles';

const CATEGORY_LABELS: Record<DebugLogCategory, string> = {
  api: 'API',
  pusher: 'Pusher',
  error: 'Error',
  message: 'Message',
  state: 'State'
};

const CATEGORY_COLORS: Record<DebugLogCategory, string> = {
  api: '#3b82f6',
  pusher: '#8b5cf6',
  error: '#ef4444',
  message: '#10b981',
  state: '#f59e0b'
};

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatSearchTime = (hour?: string, min?: string): string => {
  if (!hour) return '-';
  return `${hour}:${min || '00'}`;
};

const formatSearchType = (type?: string): string => {
  if (type === 'arrival') return '着';
  return '発';
};

const getItemTypeLabel = (item: SearchItem): string => {
  if (item.item_type === 'hotel') return 'ホテル';
  if (item.pair === 'homeward') return '復路';
  return '往路';
};

const getItemTypeColor = (item: SearchItem): string => {
  if (item.item_type === 'hotel') return '#8b5cf6';
  if (item.pair === 'homeward') return '#f59e0b';
  return '#3b82f6';
};

const getMissingFieldCategory = (field: string): 'outward' | 'homeward' | 'hotel' => {
  if (field.includes('(往路)')) return 'outward';
  if (field.includes('(復路)')) return 'homeward';
  if (field.includes('宿泊') || field.includes('チェックイン')) return 'hotel';
  return 'outward';
};

type TabType = 'logs' | 'info' | 'search';

interface Props {
  showToggleIcon?: boolean;
  proposalBundles?: ProposalBundle[];
  reservationInfo?: unknown;
}

const DebugPanel = ({ showToggleIcon = true, proposalBundles = [], reservationInfo = {} }: Props) => {
  const { logs, clearLogs, isEnabled, contextInfo, isOpen, setIsOpen } = useDebugLogContext();
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [activeTab, setActiveTab] = useState<TabType>('logs');
  const [panelPosition, setPanelPosition] = useState<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; panelX: number; panelY: number } | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<DebugLogCategory | 'all'>('all');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);
  const [conversationState, setConversationState] = useState<ConversationState | null>(null);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [stateError, setStateError] = useState<string | null>(null);

  const loadConversationState = useCallback(async () => {
    if (!contextInfo.tripId) return;
    setIsLoadingState(true);
    setStateError(null);
    try {
      const state = await fetchConversationState(contextInfo.tripId);
      setConversationState(state);
    } catch (error) {
      setStateError(error instanceof Error ? error.message : '取得に失敗しました');
    } finally {
      setIsLoadingState(false);
    }
  }, [contextInfo.tripId]);

  useEffect(() => {
    if (activeTab === 'search' && isOpen) {
      loadConversationState();
    }
  }, [activeTab, isOpen, loadConversationState]);

  const filteredLogs = useMemo(() => {
    if (categoryFilter === 'all') return logs;
    return logs.filter(log => log.category === categoryFilter);
  }, [logs, categoryFilter]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleCopyAll = useCallback(() => {
    const data = JSON.stringify(filteredLogs, null, 2);
    navigator.clipboard.writeText(data);
  }, [filteredLogs]);

  const handleCopyEntry = useCallback((entry: DebugLogEntry) => {
    const data = JSON.stringify(entry, null, 2);
    navigator.clipboard.writeText(data);
  }, []);

  const handleCopyContextInfo = useCallback(() => {
    const data = JSON.stringify(contextInfo, null, 2);
    navigator.clipboard.writeText(data);
  }, [contextInfo]);

  const handleOpenReportModal = useCallback(() => {
    setReportMessage('');
    setSubmitResult(null);
    setIsReportModalOpen(true);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
    setReportMessage('');
    setSubmitResult(null);
  }, []);

  const handleSubmitReport = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      await sendDebugReport(contextInfo.tripId, reportMessage, logs, proposalBundles, reservationInfo);
      setSubmitResult({ success: true, message: '報告を送信しました' });
      setTimeout(() => {
        handleCloseReportModal();
      }, 1500);
    } catch (error) {
      setSubmitResult({
        success: false,
        message: error instanceof Error ? error.message : '送信に失敗しました'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [contextInfo.tripId, reportMessage, logs, proposalBundles, reservationInfo, handleCloseReportModal]);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    e.preventDefault();
    isDraggingRef.current = true;
    const panelEl = (e.currentTarget as HTMLElement).parentElement;
    if (!panelEl) return;
    const rect = panelEl.getBoundingClientRect();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panelX: rect.left,
      panelY: rect.top
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !dragStartRef.current) return;
      const deltaX = moveEvent.clientX - dragStartRef.current.mouseX;
      const deltaY = moveEvent.clientY - dragStartRef.current.mouseY;
      const newX = dragStartRef.current.panelX + deltaX;
      const newY = dragStartRef.current.panelY + deltaY;
      setPanelPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      dragStartRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  if (!isEnabled) return null;

  return (
    <>
      {showToggleIcon && (
        <button type="button" style={styles.toggleButton} onClick={() => setIsOpen(!isOpen)} title="Debug Panel">
          D
        </button>
      )}
      {isOpen && (
        <div
          style={{
            ...styles.panel,
            ...(panelPosition ? { left: panelPosition.x, top: panelPosition.y, bottom: 'auto' } : {})
          }}
        >
          <div style={{ ...styles.header, cursor: 'move' }} onMouseDown={handleDragStart} role="toolbar">
            <span style={styles.title}>Debug Panel</span>
            <button type="button" style={styles.closeButton} onClick={() => setIsOpen(false)}>
              ×
            </button>
          </div>
          <div style={styles.tabBar}>
            <button
              type="button"
              style={{ ...styles.tabButton, ...(activeTab === 'logs' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('logs')}
            >
              Logs
            </button>
            <button
              type="button"
              style={{ ...styles.tabButton, ...(activeTab === 'info' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('info')}
            >
              Info
            </button>
            <button
              type="button"
              style={{ ...styles.tabButton, ...(activeTab === 'search' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('search')}
            >
              検索情報
            </button>
          </div>
          {activeTab === 'logs' && (
            <div style={styles.toolbar}>
              <select
                style={styles.filterSelect}
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value as DebugLogCategory | 'all')}
              >
                <option value="all">All</option>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <button type="button" style={styles.toolbarButton} onClick={handleCopyAll}>
                Copy All
              </button>
              <button type="button" style={styles.toolbarButton} onClick={clearLogs}>
                Clear
              </button>
              <button type="button" style={styles.reportButton} onClick={handleOpenReportModal}>
                報告
              </button>
            </div>
          )}
          {activeTab === 'info' && (
            <div style={styles.toolbar}>
              <button type="button" style={styles.toolbarButton} onClick={handleCopyContextInfo}>
                Copy Info
              </button>
            </div>
          )}
          {activeTab === 'logs' && (
            <div style={styles.logList}>
              {filteredLogs.length === 0 ? (
                <div style={styles.emptyMessage}>No logs yet</div>
              ) : (
                [...filteredLogs].reverse().map(entry => (
                  <div
                    key={entry.id}
                    style={{
                      ...styles.logEntry,
                      borderLeft: `3px solid ${CATEGORY_COLORS[entry.category]}`
                    }}
                  >
                    <div style={styles.logHeader}>
                      <button
                        type="button"
                        style={styles.logHeaderButton}
                        onClick={() => entry.details && toggleExpanded(entry.id)}
                        disabled={!entry.details}
                      >
                        <span style={styles.logTime}>{formatTime(entry.timestamp)}</span>
                        <span
                          style={{
                            ...styles.logCategory,
                            backgroundColor: CATEGORY_COLORS[entry.category]
                          }}
                        >
                          {CATEGORY_LABELS[entry.category]}
                        </span>
                        <span
                          style={{
                            ...styles.logTitle,
                            color:
                              entry.level === 'error' ? '#ef4444' : entry.level === 'warn' ? '#f59e0b' : 'inherit'
                          }}
                        >
                          {entry.title}
                        </span>
                        {entry.details && (
                          <span style={styles.expandIcon}>{expandedIds.has(entry.id) ? '▼' : '▶'}</span>
                        )}
                      </button>
                      <button
                        type="button"
                        style={styles.copyEntryButton}
                        onClick={() => handleCopyEntry(entry)}
                        title="Copy this log"
                      >
                        📋
                      </button>
                    </div>
                    {entry.summary && <div style={styles.logSummary}>{entry.summary}</div>}
                    {expandedIds.has(entry.id) && entry.details && (
                      <pre style={styles.logDetails}>{JSON.stringify(entry.details, null, 2)}</pre>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          {activeTab === 'info' && (
            <div style={styles.infoContent}>
              <div style={styles.infoSection}>
                <div style={styles.infoSectionTitle}>Trip</div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>ID:</span>
                  <span style={styles.infoValue}>{contextInfo.tripId}</span>
                </div>
                {contextInfo.trip && (
                  <>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Destination:</span>
                      <span style={styles.infoValue}>{contextInfo.trip.finalDestination || '-'}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Purpose:</span>
                      <span style={styles.infoValue}>{contextInfo.trip.purpose || '-'}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Status:</span>
                      <span style={styles.infoValue}>{contextInfo.trip.status ?? '-'}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Draft:</span>
                      <span style={styles.infoValue}>{contextInfo.trip.draft ? 'Yes' : 'No'}</span>
                    </div>
                  </>
                )}
              </div>
              {contextInfo.user && (
                <div style={styles.infoSection}>
                  <div style={styles.infoSectionTitle}>User</div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>ID:</span>
                    <span style={styles.infoValue}>{contextInfo.user.id}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Name:</span>
                    <span style={styles.infoValue}>{contextInfo.user.name}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Email:</span>
                    <span style={styles.infoValue}>{contextInfo.user.email || '-'}</span>
                  </div>
                </div>
              )}
              {contextInfo.organization && (
                <div style={styles.infoSection}>
                  <div style={styles.infoSectionTitle}>Organization</div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>ID:</span>
                    <span style={styles.infoValue}>{contextInfo.organization.id}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Name:</span>
                    <span style={styles.infoValue}>{contextInfo.organization.name}</span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>AI Reservation:</span>
                    <span style={styles.infoValue}>
                      {contextInfo.organization.aiReservationEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Workflow:</span>
                    <span style={styles.infoValue}>{contextInfo.organization.workflowStyle || '-'}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'search' && (
            <div style={styles.searchContent}>
              <div style={styles.toolbar}>
                <button type="button" style={styles.toolbarButton} onClick={loadConversationState}>
                  更新
                </button>
              </div>
              {isLoadingState && <div style={styles.emptyMessage}>読み込み中...</div>}
              {stateError && <div style={styles.errorMessage}>{stateError}</div>}
              {!isLoadingState && !stateError && conversationState && (
                <div style={styles.searchStateContent}>
                  <div style={styles.infoSection}>
                    <div style={styles.infoSectionTitle}>ステータス</div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>検索可能:</span>
                      <span
                        style={{
                          ...styles.infoValue,
                          color: conversationState.readyToSearch ? '#10b981' : '#f59e0b'
                        }}
                      >
                        {conversationState.readyToSearch ? 'はい' : 'いいえ'}
                      </span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>更新日時:</span>
                      <span style={styles.infoValue}>
                        {conversationState.updatedAt
                          ? new Date(conversationState.updatedAt).toLocaleString('ja-JP')
                          : '-'}
                      </span>
                    </div>
                  </div>
                  <div style={styles.infoSection}>
                    <div style={styles.infoSectionTitle}>検索対象</div>
                    <div style={styles.tagList}>
                      {conversationState.searchTargets.length > 0 ? (
                        conversationState.searchTargets.map(target => (
                          <span key={target} style={styles.tag}>
                            {target === 'outward' ? '往路' : target === 'homeward' ? '復路' : 'ホテル'}
                          </span>
                        ))
                      ) : (
                        <span style={styles.emptyText}>未設定</span>
                      )}
                    </div>
                  </div>
                  <div style={styles.infoSection}>
                    <div style={styles.infoSectionTitle}>不足情報</div>
                    <div style={styles.tagList}>
                      {conversationState.missingFields.length > 0 ? (
                        conversationState.missingFields.map(field => {
                          const category = getMissingFieldCategory(field);
                          const tagStyle =
                            category === 'outward'
                              ? styles.missingTagOutward
                              : category === 'homeward'
                              ? styles.missingTagHomeward
                              : styles.missingTagHotel;
                          return (
                            <span key={field} style={tagStyle}>
                              {field}
                            </span>
                          );
                        })
                      ) : (
                        <span style={styles.successText}>なし（情報完備）</span>
                      )}
                    </div>
                  </div>
                  {conversationState.dateValidationErrors.length > 0 && (
                    <div style={styles.infoSection}>
                      <div style={styles.infoSectionTitle}>日付エラー</div>
                      <div style={styles.errorList}>
                        {conversationState.dateValidationErrors.map(error => (
                          <div key={error} style={styles.errorItem}>
                            {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={styles.infoSection}>
                    <div style={styles.infoSectionTitle}>検索パラメータ</div>
                    {conversationState.currentSearchParams.items &&
                    Object.keys(conversationState.currentSearchParams.items).length > 0 ? (
                      <div style={styles.itemCardList}>
                        {Object.entries(conversationState.currentSearchParams.items).map(([key, item]) => (
                          <div
                            key={key}
                            style={{
                              ...styles.itemCard,
                              borderLeftColor: getItemTypeColor(item)
                            }}
                          >
                            <div style={styles.itemCardHeader}>
                              <span
                                style={{
                                  ...styles.itemTypeTag,
                                  backgroundColor: getItemTypeColor(item)
                                }}
                              >
                                {getItemTypeLabel(item)}
                              </span>
                            </div>
                            {item.item_type === 'transport' ? (
                              <div style={styles.itemCardBody}>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>📍</span>
                                  <span style={styles.itemLabel}>区間:</span>
                                  <span style={styles.itemValue}>
                                    {item.origin || '?'} → {item.destination || '?'}
                                  </span>
                                </div>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>📅</span>
                                  <span style={styles.itemLabel}>出発日:</span>
                                  <span style={styles.itemValue}>{item.outdate || '-'}</span>
                                </div>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>🕐</span>
                                  <span style={styles.itemLabel}>時刻:</span>
                                  <span style={styles.itemValue}>
                                    {formatSearchTime(item.outhour, item.outmin)}
                                    {item.outhour && ` (${formatSearchType(item.outtype)})`}
                                  </span>
                                </div>
                                {(item.homedate || item.homehour) && (
                                  <div style={styles.itemRow}>
                                    <span style={styles.itemIcon}>🔄</span>
                                    <span style={styles.itemLabel}>復路:</span>
                                    <span style={styles.itemValue}>
                                      {item.homedate || '-'}{' '}
                                      {item.homehour && formatSearchTime(item.homehour, item.homemin)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={styles.itemCardBody}>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>🏨</span>
                                  <span style={styles.itemLabel}>エリア:</span>
                                  <span style={styles.itemValue}>{item.destination || '-'}</span>
                                </div>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>📅</span>
                                  <span style={styles.itemLabel}>チェックイン:</span>
                                  <span style={styles.itemValue}>{item.checkin_date || item.outdate || '-'}</span>
                                </div>
                                <div style={styles.itemRow}>
                                  <span style={styles.itemIcon}>📅</span>
                                  <span style={styles.itemLabel}>チェックアウト:</span>
                                  <span style={styles.itemValue}>
                                    {item.checkout_date || item.homedate || '-'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={styles.emptyText}>パラメータなし</span>
                    )}
                  </div>
                </div>
              )}
              {!isLoadingState && !stateError && !conversationState && (
                <div style={styles.emptyMessage}>会話状態がまだ保存されていません</div>
              )}
            </div>
          )}
        </div>
      )}
      <Modal size="small" open={isReportModalOpen} onClose={handleCloseReportModal}>
        <ModalHeader>デバッグ報告</ModalHeader>
        <ModalBody>
          <div style={styles.reportModalContent}>
            <p style={styles.reportDescription}>現在のデバッグログと状態情報をSlackに報告します。</p>
            <Textarea
              value={reportMessage}
              onChange={e => setReportMessage(e.target.value)}
              placeholder="任意でメッセージを入力してください..."
              rowsMin={4}
              fullWidth
            />
            {submitResult && (
              <Alert variant={submitResult.success ? 'success' : 'danger'}>{submitResult.message}</Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="sub" onClick={handleCloseReportModal} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button color="primary" onClick={handleSubmitReport} disabled={isSubmitting}>
            {isSubmitting ? '送信中...' : '送信'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DebugPanel;
